import { useEffect, useState } from 'react';
import { Trans } from '../i18n/Trans';
import { ApiHelper } from '../helper/ApiHelper';
import { ActionButton } from '../components/ActionButton';

/**
 * 系统设置。**只有管理员进得来**（右上角那个入口就只对 admin 显示）。
 *
 * 为什么加价率必须挪到这里：它是我们的毛利，属于内部经营参数。
 * 原来它摆在客户天天用的估价页上，等于把"这个价是加了 20% 的"直接怼到客户眼前 ——
 * 这平台是拉客户来打样采购的，客户该看到的是"多快能拿到料、能不能直接下单"，
 * 不是我们赚多少。后端本来就用 quote.cost 挡住了读接口，但界面上也不该出现。
 */
export function SettingsPage() {
  const [markup, setMarkup] = useState<number | null>(null);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [order, setOrder] = useState<string[]>([]);
  const [fxDay, setFxDay] = useState('');
  const [fxSource, setFxSource] = useState('');
  const [markupNote, setMarkupNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(true);

  const takeRates = (r: { rates: Record<string, number>; day: string; source: string; order?: string[] }) => {
    setRates(r.rates);
    setFxDay(r.day);
    setFxSource(r.source);
    if (r.order) setOrder(r.order);
  };

  useEffect(() => {
    ApiHelper.call<{ percent: number }>('/api/settings/markup')
      .then((r) => setMarkup(r.percent))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
    ApiHelper.call<{ rates: Record<string, number>; day: string; source: string; order: string[] }>(
      '/api/settings/rates').then(takeRates).catch(() => undefined);
  }, []);

  /**
   * 立即刷新。**"什么都没变"这种情况必须说出来**：汇率一天最多取一次，
   * 同一天再点数据完全不变，不说话用户只会以为按钮坏了。
   */
  const refreshRates = async () => {
    const before = JSON.stringify(rates) + fxDay;
    const r = await ApiHelper.call<{ rates: Record<string, number>; day: string; source: string; order: string[] }>(
      '/api/settings/rates/refresh', 'POST');
    takeRates(r);
    const changed = JSON.stringify(r.rates) + r.day !== before;
    return {
      ok: true as const,
      msg: changed
        ? `${Trans.t('setting.ratesRefreshed')}${r.day} · ${r.source}`
        : `${Trans.t('setting.ratesSame')}${r.day || '—'}）`,
    };
  };

  const saveRates = async () => {
    const r = await ApiHelper.call<{ rates: Record<string, number>; day: string; source: string }>(
      '/api/settings/rates', 'PUT', { rates });
    takeRates({ ...r, order });
    return { ok: true as const, msg: `${Trans.t('setting.ratesSaved')} ${new Date().toTimeString().slice(0, 5)}` };
  };

  // 加价率是"改一下就自动存"，所以反馈也要就地给：存完在输入框旁边显示"加价率已保存 14:32"，
  // 让人看得出这次改动真的落库了（而不是只改了界面上的数字）。
  const save = (v: number) => {
    setMarkup(v);
    setMarkupNote('');
    setErr('');
    ApiHelper.call<{ percent: number }>('/api/settings/markup', 'PUT', { percent: v })
      .then((r) => {
        setMarkup(r.percent);
        const t = new Date().toTimeString().slice(0, 5);
        setMarkupNote(`${Trans.t('setting.markupSaved')} ${t}`);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  };

  return (
    <div>
      <h1 className="page-title">{Trans.t('nav.settings')}</h1>
      <p className="page-sub">{Trans.t('setting.sub')}</p>

      {err && <div className="error-box">{err}</div>}
      {busy && <p className="hint">{Trans.t('common.loading')}</p>}

      {!busy && markup !== null && (
        <div className="setting-item">
          <div>
            <div className="setting-name">{Trans.t('quote.markup')}</div>
            <div className="hint">{Trans.t('setting.markupHint')}</div>
          </div>
          <div className="setting-ctl">
            <input
              type="number" min={0} max={500} step={0.5}
              value={markup}
              onChange={(e) => save(Number(e.target.value))}
            />
            <span className="hint">{Trans.t('quote.markupUnit')}</span>
            {markupNote && <span className="action-note ok">{markupNote}</span>}
          </div>
        </div>
      )}

      {order.length > 0 && (
        <div className="setting-item fx-block">
          <div>
            <div className="setting-name">{Trans.t('setting.rates')}</div>
            <div className="hint">{Trans.t('setting.ratesHint')}</div>
            <div className="hint">
              {Trans.t('setting.ratesMeta')}: {fxDay || '—'} · {fxSource}
            </div>
          </div>
          <div className="fx-grid">
            {order.map((ccy) => (
              <label className="fx-cell" key={ccy}>
                <span>1 {ccy} =</span>
                <input
                  type="number" step="0.0001" min="0"
                  value={rates[ccy] ?? ''}
                  onChange={(e) => setRates({ ...rates, [ccy]: Number(e.target.value) })}
                />
                <span>USD</span>
              </label>
            ))}
            <div className="fx-actions">
              <ActionButton onRun={saveRates} busyLabel={Trans.t('setting.savingRates')}>
                {Trans.t('setting.ratesSave')}
              </ActionButton>
              <ActionButton
                className="btn ghost"
                onRun={refreshRates}
                busyLabel={Trans.t('setting.refreshing')}
              >
                {Trans.t('setting.ratesRefresh')}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
